import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Wind, Brain, Heart, Sparkles, Clock, ChevronRight } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Exercises = () => {
  const { language } = useLanguage();
  const [exercises, setExercises] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchExercises();
  }, []);

  const fetchExercises = async () => {
    try {
      const response = await axios.get(`${API_URL}/exercises`);
      setExercises(response.data);
    } catch (error) {
      console.error('Failed to fetch exercises:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', label_fr: 'Tous', label_en: 'All', icon: Sparkles, color: 'bg-gray-100 text-gray-600' },
    { id: 'breathing', label_fr: 'Respiration', label_en: 'Breathing', icon: Wind, color: 'bg-blue-100 text-blue-600' },
    { id: 'cognitive', label_fr: 'Cognitif', label_en: 'Cognitive', icon: Brain, color: 'bg-purple-100 text-purple-600' },
    { id: 'behavioral', label_fr: 'Comportemental', label_en: 'Behavioral', icon: Heart, color: 'bg-pink-100 text-pink-600' },
    { id: 'relaxation', label_fr: 'Relaxation', label_en: 'Relaxation', icon: Sparkles, color: 'bg-green-100 text-green-600' },
    { id: 'mindfulness', label_fr: 'Pleine conscience', label_en: 'Mindfulness', icon: Sparkles, color: 'bg-yellow-100 text-yellow-600' }
  ];

  const filteredExercises = selectedCategory === 'all' 
    ? exercises 
    : exercises.filter(ex => ex.category === selectedCategory);

  const getCategoryInfo = (categoryId) => {
    return categories.find(c => c.id === categoryId) || categories[0];
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-[#F9FAFB]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#F9FAFB] py-8 sm:py-12 px-4" data-testid="exercises-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 animate-fade-in-up">
          <Link to="/dashboard">
            <Button variant="ghost" className="rounded-full mb-2">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Retour' : 'Back'}
            </Button>
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
            {language === 'fr' ? 'Exercices TCC' : 'CBT Exercises'}
          </h1>
          <p className="text-gray-600 mt-1">
            {language === 'fr' 
              ? 'Techniques comportementales et cognitives pour vous aider à arrêter de fumer.'
              : 'Behavioral and cognitive techniques to help you quit smoking.'}
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8 animate-fade-in-up">
          {categories.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                  selectedCategory === category.id 
                    ? 'bg-primary text-white shadow-lg' 
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
                data-testid={`category-${category.id}`}
              >
                <Icon className="w-4 h-4" />
                <span className="font-medium">
                  {language === 'fr' ? category.label_fr : category.label_en}
                </span>
              </button>
            );
          })}
        </div>

        {/* Exercise Detail Modal */}
        {selectedExercise && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedExercise(null)}>
            <Card 
              className="glass-card rounded-3xl p-8 max-w-lg w-full max-h-[80vh] overflow-y-auto animate-scale-in"
              onClick={(e) => e.stopPropagation()}
              data-testid="exercise-modal"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${getCategoryInfo(selectedExercise.category).color}`}>
                  {React.createElement(getCategoryInfo(selectedExercise.category).icon, { className: "w-7 h-7" })}
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {language === 'fr' ? selectedExercise.title_fr : selectedExercise.title_en}
                  </h2>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                    <Clock className="w-4 h-4" />
                    <span>{selectedExercise.duration_minutes} min</span>
                  </div>
                </div>
              </div>

              <div className="prose prose-gray">
                <p className="text-gray-700 leading-relaxed text-lg">
                  {language === 'fr' ? selectedExercise.description_fr : selectedExercise.description_en}
                </p>
              </div>

              <div className="mt-8 flex gap-4">
                <Button 
                  onClick={() => setSelectedExercise(null)}
                  className="flex-1 rounded-full bg-primary hover:bg-primary/90"
                  data-testid="close-exercise-btn"
                >
                  {language === 'fr' ? 'Terminé' : 'Done'}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Exercises Grid */}
        <div className="grid gap-4">
          {filteredExercises.map((exercise, index) => {
            const categoryInfo = getCategoryInfo(exercise.category);
            const Icon = categoryInfo.icon;
            
            return (
              <Card 
                key={exercise.id}
                className="glass-card rounded-3xl p-6 cursor-pointer hover:shadow-lg hover:translate-y-[-2px] transition-all animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => setSelectedExercise(exercise)}
                data-testid={`exercise-${exercise.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${categoryInfo.color}`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900">
                      {language === 'fr' ? exercise.title_fr : exercise.title_en}
                    </h3>
                    <div className="flex items-center gap-4 mt-1">
                      <span className="text-sm text-gray-500 capitalize">
                        {language === 'fr' ? categoryInfo.label_fr : categoryInfo.label_en}
                      </span>
                      <span className="flex items-center gap-1 text-sm text-gray-500">
                        <Clock className="w-3 h-3" />
                        {exercise.duration_minutes} min
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </Card>
            );
          })}
        </div>

        {filteredExercises.length === 0 && (
          <Card className="glass-card rounded-3xl p-12 text-center">
            <p className="text-gray-500">
              {language === 'fr' ? 'Aucun exercice dans cette catégorie.' : 'No exercises in this category.'}
            </p>
          </Card>
        )}
      </div>
    </div>
  );
};
